import React, {useState, useRef, useEffect } from 'react';
import dayjs from 'dayjs';

import { Box, IconButton, CircularProgress } from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import { VictoryChart, VictoryLine, VictoryAxis, VictoryTheme, VictoryVoronoiContainer, VictoryTooltip, VictoryScatter, VictoryGroup } from 'victory';
import * as d3 from 'd3-scale-chromatic';


// interface Point {
//     x: number;
//     y: number;
//     prefix?: string;
//     suffix?: string;
// }

// interface LinesProps {
//     data: any[],
//     key: string,
//     label: string,
//     showDisabled?: boolean;
// }


const generateColorScheme = (numColors) => {
    const colorScale = d3.schemeTableau10.concat(d3.schemeSet3);
    return colorScale.slice(0, numColors);
};

const MultiLineChart = ({ 
    lines=[], filter,
    hideLegend=false, onLegendClick,
    isLoading,
}) => {

    const chartRef = useRef(null);
    const filterRef = useRef(null);

    const [filterOpen, setFilterOpen] = useState(false);
    const [chartHeight, setChartHeight] = useState(0);

    const [config, setConfig] = useState({
        hide_line: {},
    });

    const toggleFilter = () => {
        setFilterOpen(!filterOpen);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (filterRef.current && !filterRef.current.contains(event.target)) {
                const rect = filterRef.current.getBoundingClientRect();
                const topLeft = { x: rect.left, y: rect.top };
                const bottomRight = { x: rect.right, y: rect.bottom };
                if (event.clientX < topLeft.x || 
                    event.clientX > bottomRight.x) {
                    setFilterOpen(false);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const resizeObserver = new ResizeObserver(entries => {
          for (let entry of entries) {
            setChartHeight(entry.contentRect.height);
          }
        });
    
        if (chartRef.current) {
          resizeObserver.observe(chartRef.current);
        }
    
        return () => {
          if (chartRef.current) {
            resizeObserver.unobserve(chartRef.current);
          }
        };
      }, []);

    const toggleVisibilityForLine = (key) => {
        setConfig({
            ...config,
            hide_line: {
                ...config.hide_line,
                [key]: !config.hide_line[key],
            }
        });
    };

    const colorScheme = generateColorScheme(lines.length);
    const colorMap = lines.reduce((acc, line, index) => {
        acc[line.key] = colorScheme[index % colorScheme.length];
        return acc;
    }, {});

    return (
        <Box>
        <Box 
        sx={{
            display: 'flex', 
            position: 'relative',
            width: '100%',
            transition: 'flex-grow 0.3s',
        }} 
        >
            <Box ref={chartRef} width='100%' sx={{backgroundColor: 'var(--background-color)', pr: 5}}>
                {
                isLoading ?
                <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: 500}}>
                    <CircularProgress />
                </Box>
                :
                <VictoryChart 
                width={1500} 
                height={700}
                padding={{ top: 50, bottom: 100, left: 75, right: 50 }}
                theme={VictoryTheme.material}
                scale={{ x: "time", y: "linear" }}
                containerComponent={
                    <VictoryVoronoiContainer
                      labels={({ datum }) => !datum.hideLabel ? `${datum.prefix}: ${(datum.y).toFixed(2)}${datum.suffix || ''}\n${dayjs(datum.x).format('MM/DD/YYYY HH:mm')}` : null}
                      labelComponent={
                        <VictoryTooltip 
                          style={{ fontSize: 20 }}
                          flyoutStyle={{ fill: "white" }}
                        />
                      }
                    />
                  }
                >
                <VictoryAxis
                    tickFormat={(t) => dayjs(t).format('MM/DD/YYYY')}
                    style={{
                    axisLabel: { padding: 50 },
                    tickLabels: { textAnchor: 'end', fontSize: 20, },
                    }}
                />
                <VictoryAxis
                dependentAxis
                style={{
                  axisLabel: { padding: 40 },
                  tickLabels: { fontSize: 20 },
                }}
              />
                {
                    lines.map((line) => !config.hide_line[line.key] ? (
                        <VictoryGroup 
                        key={line.key}
                        color={colorMap[line.key]}
                        >
                            <VictoryLine  
                            data={line.data.map((d) => ({...d, hideLabel: true}))}
                            />
                            <VictoryScatter
                            data={line.data}
                            size={3}
                            />
                        </VictoryGroup>
                    ) : null)
                }
                </VictoryChart>
                }
            </Box>
            {
            filter ?
            <Box
            ref={filterRef}
            sx={{
                width: filterOpen ? 350 : 30,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                border: 1,
                borderRadius: !filterOpen ? 1 : 3,
                mt: '10px',
                mr: 1,
                minHeight: chartHeight - 20,
                maxHeight: !filterOpen ? chartHeight - 20 : null,
                backgroundColor: !filterOpen ? 'var(--primary-color)' : 'var(--background-color)',
                borderColor: 'var(--primary-color)',
                px: 2,

                position: 'absolute',
                zIndex: 5,
                right: 0,
                top: 0,
                boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
            }}
            >

            <Box sx={{mb: 1}}>
            <IconButton onClick={() => toggleFilter()}>
                <FilterAltIcon sx={{ color: !filterOpen ? 'var(--background-color)' : 'var(--primary-color)' }} />
            </IconButton>
            </Box>

            <Box
            sx={{
                mb: 2,
                width: '100%',
            }}
            >
            {
                filterOpen ? filter : null
            }
            </Box>
            </Box>
            : null
            }
        </Box>

        <Box>
        {
            !hideLegend ?
            <Box sx={{
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 2, 
                borderTop: 1,
                borderBottom: 1, 
                p: 2,
                mt: 2,
            }}>
                {
                    lines.map((line) => (
                        <Box 
                        key={line.key} 
                        sx={{
                            display: 'flex', 
                            alignItems: 'center', 
                            border: 1, 
                            borderColor: colorMap[line.key],
                            px: 1, borderRadius: 2,
                            cursor: onLegendClick ? 'pointer' : 'default',
                            backgroundColor: line.showDisabled ? 'rgba(0, 0, 0, 0.1)' : 'var(--background-color)',
                            color: 'var(--background-color)',
                        }}
                        onClick={() => onLegendClick ? onLegendClick(line.key) : null}
                        >
                            <Box sx={{width: 10, height: 10, borderRadius: '50%', backgroundColor: colorMap[line.key], mr: 1}} />
                            <Box sx={{fontSize: 14, fontWeight: 500, fontWeight: 'bold', color: 'var(--text-color)'}}>{line.label || line.key}</Box>
                            <Box>
                            <IconButton onClick={(e) => {
                                e.stopPropagation();
                                toggleVisibilityForLine(line.key);
                            }}>
                                {config.hide_line[line.key] ? 
                                <VisibilityOffIcon sx={{ color: 'var(--accent-color)' }} /> :
                                <VisibilityIcon sx={{ color: 'var(--text-color)' }}/>
                            }
                            </IconButton>
                            </Box>
                        </Box>
                    ))
                }
            </Box>
            : null
        }
        </Box>
        </Box>
    )

}

export default MultiLineChart;